import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { messageService } from '@/services/api';

interface MessageContextValue {
  /** Unread message count keyed by conversation id. */
  unreadByConversation: Record<string, number>;
  totalUnread: number;
  refresh: () => Promise<void>;
  markConversationRead: (conversationId: string) => Promise<void>;
  /** Thread currently on screen; incoming messages there are auto-read. */
  setActiveConversation: (conversationId: string | null) => void;
}

const MessageContext = createContext<MessageContextValue | undefined>(undefined);

export function MessageProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [unreadByConversation, setUnreadByConversation] = useState<Record<string, number>>({});

  const activeRef = useRef<string | null>(null);
  const nameCacheRef = useRef<Record<string, string>>({});

  const refresh = useCallback(async () => {
    if (!user) {
      setUnreadByConversation({});
      return;
    }
    const { data, error } = await messageService.getUnreadCounts();
    if (error) return;
    const next: Record<string, number> = {};
    for (const row of data || []) {
      const count = Number(row.unread_count) || 0;
      if (count > 0) next[row.conversation_id] = count;
    }
    setUnreadByConversation(next);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markConversationRead = useCallback(async (conversationId: string) => {
    setUnreadByConversation((prev) => {
      if (!prev[conversationId]) return prev;
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
    await messageService.markConversationRead(conversationId);
  }, []);

  const setActiveConversation = useCallback((conversationId: string | null) => {
    activeRef.current = conversationId;
  }, []);

  // One app-wide subscription: RLS limits rows to conversations we belong to.
  useEffect(() => {
    if (!user?.id) {
      setUnreadByConversation({});
      return;
    }

    const channel = supabase
      .channel('messages-inbox')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload: any) => {
          const row = payload.new;
          if (!row?.conversation_id || row.sender_id === user.id) return;

          // Call history is written for both sides when a call ends. Only a call
          // the callee missed should alert them; a call they were on should not.
          if (row.kind === 'call' && row.counts_as_unread === false) {
            if (activeRef.current === row.conversation_id) {
              void messageService.markConversationRead(row.conversation_id);
            }
            return;
          }

          // Reading it right now: keep it read instead of incrementing.
          if (activeRef.current === row.conversation_id) {
            void messageService.markConversationRead(row.conversation_id);
            return;
          }

          setUnreadByConversation((prev) => ({
            ...prev,
            [row.conversation_id]: (prev[row.conversation_id] || 0) + 1,
          }));

          // Play new message sound.
          try {
            const audio = new Audio('/audio/message.mp3');
            audio.volume = 0.7;
            void audio.play().catch(() => {
              // Autoplay blocked or file missing; silent fallback.
            });
          } catch {
            // Audio not supported.
          }

          void (async () => {
            let name = nameCacheRef.current[row.sender_id];
            if (!name) {
              const { data: sender } = await (supabase as any)
                .from('profiles')
                .select('display_name, username')
                .eq('id', row.sender_id)
                .maybeSingle();
              name = sender?.display_name || sender?.username || 'Someone';
              nameCacheRef.current[row.sender_id] = name;
            }
            const preview = (row.content || 'New message').slice(0, 80);
            toast(`New message from ${name}`, preview);
          })();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast, user?.id]);

  const totalUnread = useMemo(
    () => Object.values(unreadByConversation).reduce((sum, count) => sum + count, 0),
    [unreadByConversation],
  );

  return (
    <MessageContext.Provider
      value={{
        unreadByConversation,
        totalUnread,
        refresh,
        markConversationRead,
        setActiveConversation,
      }}
    >
      {children}
    </MessageContext.Provider>
  );
}

export function useMessages() {
  const context = useContext(MessageContext);
  if (!context) throw new Error('useMessages must be used within a MessageProvider');
  return context;
}
